import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import HotelCard from "../components/HotelCard";
import SearchBox from "../components/SearchBox";
import { mockHotels } from "../data/mockData";
import { Hotel } from "../types";
import { Filter, Grid, List, Star } from "lucide-react";

const SearchResultsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [hotels] = useState<Hotel[]>(mockHotels);
  const [filteredHotels, setFilteredHotels] = useState<Hotel[]>(mockHotels);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("price-low");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [filters, setFilters] = useState({
    priceRange: [0, 500],
    stars: [] as number[],
    amenities: [] as string[],
  });

  const location = searchParams.get("location") || "";

  // Filter and sort hotels
  useEffect(() => {
    let filtered = [...hotels];

    // Apply location filter
    if (location) {
      filtered = filtered.filter(
        (hotel) =>
          hotel.city.toLowerCase().includes(location.toLowerCase()) ||
          hotel.country.toLowerCase().includes(location.toLowerCase()) ||
          hotel.location.toLowerCase().includes(location.toLowerCase())
      );
    }

    // Apply filters
    filtered = filtered.filter((hotel) => {
      // Price filter
      if (
        hotel.price < filters.priceRange[0] ||
        hotel.price > filters.priceRange[1]
      ) {
        return false;
      }

      // Stars filter
      if (filters.stars.length > 0 && !filters.stars.includes(hotel.stars)) {
        return false;
      }

      // Amenities filter
      if (filters.amenities.length > 0) {
        const hasAllAmenities = filters.amenities.every((amenity) =>
          hotel.amenities.includes(amenity)
        );
        if (!hasAllAmenities) return false;
      }

      return true;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "price-low":
          return a.price - b.price;
        case "price-high":
          return b.price - a.price;
        case "rating":
          return b.rating - a.rating;
        case "stars":
          return b.stars - a.stars;
        default:
          return 0;
      }
    });

    setFilteredHotels(filtered);
  }, [hotels, filters, sortBy, location]);

  const handleFilterChange = (filterType: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      priceRange: [0, 500],
      stars: [],
      amenities: [],
    });
  };

  const amenityOptions = [
    "Free WiFi",
    "Pool",
    "Restaurant",
    "Fitness Center",
    "Airport Shuttle",
    "Spa",
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Bar */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <SearchBox variant="compact" />
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar */}
          <div
            className={`lg:w-80 ${showFilters ? "block" : "hidden lg:block"}`}
          >
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Filters</h3>
                <button
                  onClick={clearFilters}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Clear all
                </button>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Price Range</h4>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="500"
                    value={filters.priceRange[1]}
                    onChange={(e) =>
                      handleFilterChange("priceRange", [
                        filters.priceRange[0],
                        parseInt(e.target.value),
                      ])
                    }
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>${filters.priceRange[0]}</span>
                    <span>${filters.priceRange[1]}</span>
                  </div>
                </div>
              </div>

              {/* Star Rating */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Star Rating</h4>
                <div className="space-y-2">
                  {[5, 4, 3, 2, 1].map((stars) => (
                    <label key={stars} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.stars.includes(stars)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFilterChange("stars", [
                              ...filters.stars,
                              stars,
                            ]);
                          } else {
                            handleFilterChange(
                              "stars",
                              filters.stars.filter((s) => s !== stars)
                            );
                          }
                        }}
                        className="mr-3"
                      />
                      <div className="flex items-center">
                        {Array.from({ length: stars }, (_, i) => (
                          <Star
                            key={i}
                            className="w-4 h-4 text-yellow-400 fill-current"
                          />
                        ))}
                        <span className="ml-2 text-sm">{stars} stars</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Amenities */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Amenities</h4>
                <div className="space-y-2">
                  {amenityOptions.map((amenity) => (
                    <label key={amenity} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.amenities.includes(amenity)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFilterChange("amenities", [
                              ...filters.amenities,
                              amenity,
                            ]);
                          } else {
                            handleFilterChange(
                              "amenities",
                              filters.amenities.filter((a) => a !== amenity)
                            );
                          }
                        }}
                        className="mr-3"
                      />
                      <span className="text-sm">{amenity}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {filteredHotels.length} hotels found
                </h2>
                {location && <p className="text-gray-600">in {location}</p>}
              </div>

              <div className="flex items-center space-x-4 mt-4 sm:mt-0">
                {/* Mobile Filter Button */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden btn btn-secondary flex items-center space-x-2"
                >
                  <Filter className="w-4 h-4" />
                  <span>Filters</span>
                </button>

                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                >
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Rating</option>
                  <option value="stars">Stars</option>
                </select>

                {/* View Mode */}
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2 ${
                      viewMode === "grid"
                        ? "bg-red-600 text-white"
                        : "bg-white text-gray-600"
                    }`}
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 ${
                      viewMode === "list"
                        ? "bg-red-600 text-white"
                        : "bg-white text-gray-600"
                    }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Hotels Grid/List */}
            <div
              className={`${
                viewMode === "grid"
                  ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                  : "space-y-4"
              }`}
            >
              {filteredHotels.map((hotel) => (
                <HotelCard key={hotel.id} hotel={hotel} />
              ))}
            </div>

            {filteredHotels.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <Filter className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No hotels found
                </h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your filters or search criteria
                </p>
                <button onClick={clearFilters} className="btn btn-primary">
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;
